import { Component, Inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
    ProjectManagementService,
    Project,
    ProjectCreateRequest,
    ProjectCompetitor,
    IndustryItem,
    CategoryItem,
    SuggestBrandDetailsResponse,
} from '../../services/project-management.service';
import { CountryService } from '../../services/country.service';
import { LanguageService, LanguageOption } from '../../services/language.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonConfirmDialogComponent } from '../common-confirm-dialog/common-confirm-dialog.component';

export interface ProjectDialogData {
    mode: 'create' | 'edit';
    project?: Project;
    projectdataCount?: number;
}

/** Result when dialog closes after create: project + optional createAndAnalyze (navigate to analysis) */
export interface ProjectDialogResult {
    project: Project;
    createAndAnalyze?: boolean;
}

@Component({
    selector: 'app-project-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatProgressSpinnerModule,
        MatStepperModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
    ],
    templateUrl: './project-dialog.component.html',
    styleUrl: './project-dialog.component.scss',
})
export class ProjectDialogComponent {
    readonly MAX_COMPETITORS = 5;
    /** Max categories for Oxylabs/overview analysis (primary + extras, padded at save if needed) */
    readonly MAX_ANALYSIS_CATEGORIES = 5;
    readonly mode: 'create' | 'edit' = 'create';
    readonly project: Project | undefined;
    readonly projectdataCount: number = 0;

    form = {
        name: '',
        description: '',
        primary_country: 'Global',
        language: 'English' as string,
        competitors: [] as ProjectCompetitor[],
        industries: [] as IndustryItem[],
        categories: [] as CategoryItem[],
        /** Selected industry names (multi-select). */
        selectedIndustryNames: [] as string[],
        /** Selected category names (multi-select). */
        selectedCategoryNames: [] as string[],
    };

    countryOptions: string[] = [];
    languageOptions: LanguageOption[] = [];
    suggestBrandDetailsResult: SuggestBrandDetailsResponse | null = null;
    suggestBrandDetailsLoading = false;
    saving = false;
    editFormLoading = false;
    /** Step completion for strict sequential (linear) navigation. Step 2 disabled until Step 1 completed, etc. */
    step1Completed = false;
    step2Completed = false;
    // step3Completed = false;

    constructor(
        private dialogRef: MatDialogRef<ProjectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: ProjectDialogData,
        private projectService: ProjectManagementService,
        private countryService: CountryService,
        private languageService: LanguageService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
    ) {
        this.mode = data.mode || 'create';
        this.project = data.project;
        this.projectdataCount = data.projectdataCount ?? 0;

        if (this.mode === 'create') {
            this.form = {
                name: '',
                description: '',
                primary_country: 'Global',
                language: 'English',
                competitors: [],
                industries: [],
                categories: [],
                selectedIndustryNames: [],
                selectedCategoryNames: [],
            };
            this.loadCountryOptions();
            this.loadLanguageOptions();
        } else if (this.project) {
            // Prefer full industries/categories from API so all options show; fallback to comma-separated strings
            const industryNames = this.splitCommaSeparated(this.project.industry);
            const categoryNames = this.splitCommaSeparated(this.project.category);
            const industries: IndustryItem[] = (this.project.industries?.length ?? 0) > 0
                ? this.project.industries!.map((i) => ({ name: i.name, isPrimary: !!i.isPrimary }))
                : industryNames.length
                    ? industryNames.map((name) => ({ name, isPrimary: true }))
                    : [];
            const primaryCategoryName = (this.project.category || '').trim()
                || (this.project.categories?.find((c) => c.isPrimary)?.name || '').trim()
                || (categoryNames[0] || '').trim()
                || 'General';
            const categories: CategoryItem[] = [{ name: primaryCategoryName, isPrimary: true }];
            const selectedIndustryNames = industries.filter((i) => i.isPrimary).map((i) => i.name).length > 0
                ? industries.filter((i) => i.isPrimary).map((i) => i.name)
                : industryNames.length ? industryNames : industries.map((i) => i.name);
            const selectedCategoryNames = [primaryCategoryName];
            this.form = {
                name: this.project.name,
                description: this.project.description ?? '',
                primary_country: this.project.primary_country || 'Global',
                language: this.project.language || 'English',
                competitors: (this.project.competitors && this.project.competitors.length > 0)
                    ? this.project.competitors.map((c) => ({ name: c.name ?? '', url: c.url ?? '' }))
                    : [],
                industries,
                categories,
                selectedIndustryNames,
                selectedCategoryNames,
            };
            this.loadCountryOptions();
            this.loadLanguageOptions();
        } else {
            this.editFormLoading = true;
            this.loadCountryOptions();
            this.loadLanguageOptions();
        }
    }

    /** Split comma-separated string into trimmed non-empty values (used for industry/category from backend). */
    private splitCommaSeparated(value: string | undefined | null): string[] {
        if (value == null || typeof value !== 'string') return [];
        return value.split(',').map((s) => s.trim()).filter(Boolean);
    }

    onCategoriesChange(value: string[]): void {
        if (this.mode === 'edit') {
            const primaryCategory = this.form.categories.find((c) => c.isPrimary)?.name
                || this.form.selectedCategoryNames[0]
                || 'General';
            this.form.selectedCategoryNames = [primaryCategory];
            this.cdr.markForCheck();
            return;
        }
        if (value.length > this.MAX_ANALYSIS_CATEGORIES) {
            this.form.selectedCategoryNames = value.slice(0, this.MAX_ANALYSIS_CATEGORIES);
            this.snackBar.open(
                `Select up to ${this.MAX_ANALYSIS_CATEGORIES} categories for Oxylabs/overview analysis.`,
                'Close',
                { duration: 4500 },
            );
        } else {
            this.form.selectedCategoryNames = value;
        }
        this.cdr.markForCheck();
    }

    /** Categories persisted for overview Oxylabs; pads from verify suggestions if user picked fewer than max */
    private buildAnalysisCategoriesForSave(): string[] {
        const max = this.MAX_ANALYSIS_CATEGORIES;
        const selected = [...this.form.selectedCategoryNames].map((s) => s.trim()).filter(Boolean);
        const pool =
            this.suggestBrandDetailsResult?.categories?.length && this.mode === 'create'
                ? [...this.suggestBrandDetailsResult.categories]
                : this.form.categories.map((c) => c.name);
        const out: string[] = [];
        const push = (x: string) => {
            const t = x.trim();
            if (t && !out.includes(t)) out.push(t);
        };
        if (selected.length >= max) {
            for (const s of selected) {
                push(s);
                if (out.length >= max) return out;
            }
            return out;
        }
        for (const s of selected) push(s);
        for (const c of pool) {
            push(c);
            if (out.length >= max) break;
        }
        return out.slice(0, max);
    }

    get title(): string {
        return this.mode === 'create' ? 'Create New Project' : 'Edit Project';
    }

    get submitLabel(): string {
        if (this.saving) return this.mode === 'create' ? 'Creating...' : 'Updating...';
        return this.mode === 'create' ? 'Create Project' : 'Update Project';
    }

    loadCountryOptions(): void {
        this.countryService.getCountryOptions().subscribe({
            next: (countries) => (this.countryOptions = countries),
            error: () => (this.countryOptions = this.countryService.getDefaultCountries()),
        });
    }

    loadLanguageOptions(): void {
        this.languageService.getLanguageOptions().subscribe({
            next: (options) => (this.languageOptions = options),
        });
    }

    addCompetitor(): void {
        if (this.form.competitors.length >= this.MAX_COMPETITORS) return;
        this.form.competitors.push({ name: '', url: '' });
    }

    removeCompetitor(index: number): void {
        this.form.competitors.splice(index, 1);
    }

    /** Call verify API and optionally move to next step (step 3). */
    verifyAndExplore(stepper?: MatStepper): void {
        const name = (this.form.name || '').trim();
        if (!name) {
            this.snackBar.open('Enter brand name first', 'Close', { duration: 3000 });
            return;
        }
        this.suggestBrandDetailsLoading = true;
        this.suggestBrandDetailsResult = null;
        this.projectService
            .suggestBrandDetails({
                brand_name: name,
                brand_domain: (this.form.description || '').trim() || undefined,
                market: this.form.primary_country || 'Global',
                language: this.form.language || 'English',
            })
            .subscribe({
                next: (res) => {
                    this.suggestBrandDetailsLoading = false;
                    this.suggestBrandDetailsResult = res;
                    // Build industries/categories options; pre-select first as default
                    if (res.industries?.length) {
                        this.form.industries = res.industries.map((name) => ({ name, isPrimary: true }));
                        this.form.selectedIndustryNames = [res.industries[0]];
                    } else {
                        this.form.industries = [];
                        this.form.selectedIndustryNames = [];
                    }
                    if (res.categories?.length) {
                        this.form.categories = res.categories.map((name) => ({ name, isPrimary: true }));
                        this.form.selectedCategoryNames = [res.categories[0]];
                    } else {
                        this.form.categories = [];
                        this.form.selectedCategoryNames = [];
                    }
                    // Pre-fill competitors from API when current list is empty (high first, then low, max MAX_COMPETITORS)
                    // if (this.form.competitors.length === 0 && (res.competitors_high?.length || res.competitors_low?.length)) {
                    //     const high = (res.competitors_high || []).map((c) => ({ name: c.name, url: c.url || '' }));
                    //     const low = (res.competitors_low || []).map((c) => ({ name: c.name, url: c.url || '' }));
                    //     this.form.competitors = [...high, ...low].slice(0, this.MAX_COMPETITORS);
                    // }
                    this.snackBar.open(
                        res.cached ? 'Suggestions loaded (cached)' : 'Suggestions loaded. Review below.',
                        'Close',
                        { duration: 4000 },
                    );
                    if (stepper) {
                        this.step2Completed = true;
                        stepper.next();
                    }
                },
                error: (err) => {
                    this.suggestBrandDetailsLoading = false;
                    this.form.selectedIndustryNames = [];
                    this.form.selectedCategoryNames = [];
                    this.suggestBrandDetailsResult = {
                        industries: [],
                        categories: [],
                        competitors_high: [],
                        competitors_low: [],
                        error: err?.error?.detail || 'Failed to get suggestions',
                    };
                    this.snackBar.open(this.suggestBrandDetailsResult.error!, 'Close', { duration: 5000 });
                },
            });
    }

    /** Step 1 Next: mark completed and advance (strict sequential). Flush binding so linear stepper sees completed on first click. */
    onStep1Next(stepper: MatStepper): void {
        if (!this.form.name?.trim() || !this.form.description?.trim()) return;
        this.step1Completed = true;
        this.cdr.detectChanges(); // so [completed] is updated before stepper.next() runs
        stepper.next();
    }

    /** Called when leaving step 2 (Verify): change to step 3 first, then fire verify API so loading shows on step 3. */
    onStep2Next(stepper: MatStepper): void {
        this.step2Completed = true;
        this.cdr.detectChanges();
        stepper.next();
        setTimeout(() => this.verifyAndExplore(), 0); // run after step transition so Step 3 is visible before API starts
    }

    /** Step 3 Next: mark completed and advance. */
    // onStep3Next(stepper: MatStepper): void {
    //     if (!this.form.selectedIndustryNames?.length || !this.form.selectedCategoryNames?.length) return;
    //     this.step3Completed = true;
    //     this.cdr.detectChanges();
    //     stepper.next();
    // }

    cancel(): void {
        this.dialogRef.close();
    }

    submit(createAndAnalyze = false): void {
        if (this.mode === 'create') {
            this.doCreate(createAndAnalyze);
        } else {
            this.doUpdate();
        }
    }

    private doCreate(createAndAnalyze: boolean): void {
        // const competitors = this.form.competitors
        //     .filter((c) => c.name?.trim() && c.url?.trim())
        //     .slice(0, this.MAX_COMPETITORS);
        const request: ProjectCreateRequest = {
            name: this.form.name,
            description: this.form.description,
            primary_country: this.form.primary_country || 'Global',
            language: this.form.language || 'English',
            // competitors,
        };
        // Send industry/category as comma-separated for backward compat; send full industries/categories with isPrimary from selection
        if (this.form.selectedIndustryNames.length) {
            request.industry = this.form.selectedIndustryNames.join(',');
        }
        if (this.form.selectedCategoryNames.length) {
            request.category = this.form.selectedCategoryNames[0];
        }
        if (this.form.industries.length) {
            request.industries = this.form.industries.map((i) => ({
                name: i.name,
                isPrimary: this.form.selectedIndustryNames.includes(i.name),
            }));
        }
        if (this.form.categories.length) {
            request.categories = this.form.categories.map((c) => ({
                name: c.name,
                isPrimary: c.name === request.category,
            }));
        }
        request.analysis_categories = this.buildAnalysisCategoriesForSave();

        this.saving = true;
        this.projectService.createProject(request).subscribe({
            next: (project) => {
                this.saving = false;
                this.dialogRef.close({ project, createAndAnalyze } as ProjectDialogResult);
            },
            error: (err) => {
                console.error('Error creating project:', err);
                this.saving = false;
                this.snackBar.open(err?.error?.detail || 'Failed to create project', 'Close', { duration: 5000 });
            },
        });
    }

    private doUpdate(): void {
        if (!this.project) return;
        const hasVersions = this.projectdataCount > 0;
        if (hasVersions) {
            const ref = this.dialog.open(CommonConfirmDialogComponent, {
                width: '450px',
                disableClose: true,
                data: {
                    title: 'Are you sure want to update project?',
                    description:
                        'It will delete the older analysis and you need to again do analysis for the updated changes.',
                },
            });
            ref.afterClosed().subscribe((confirmed) => {
                if (confirmed) this.performUpdate();
            });
            return;
        }
        this.performUpdate();
    }

    private performUpdate(): void {
        if (!this.project) return;
        const competitors = this.form.competitors
            .filter((c) => c.name?.trim() && c.url?.trim())
            .slice(0, this.MAX_COMPETITORS);
        const request: ProjectCreateRequest = {
            name: this.form.name,
            description: this.form.description,
            primary_country: this.form.primary_country || 'Global',
            language: this.form.language || 'English',
            competitors,
        };
        // Send industry/category as comma-separated for backward compat; send full industries/categories with isPrimary from selection
        if (this.form.selectedIndustryNames.length) {
            request.industry = this.form.selectedIndustryNames.join(',');
        }
        if (this.form.selectedCategoryNames.length) {
            request.category = this.form.selectedCategoryNames[0];
        } else if (this.project?.category) {
            request.category = this.project.category;
        }
        if (this.form.industries.length) {
            request.industries = this.form.industries.map((i) => ({
                name: i.name,
                isPrimary: this.form.selectedIndustryNames.includes(i.name),
            }));
        }
        // Edit flow should rely on primary `category` key only.
        // Keep existing analysis_categories unless explicitly changed in create flow.
        request.analysis_categories = this.project?.analysis_categories || this.buildAnalysisCategoriesForSave();

        this.saving = true;
        this.projectService.updateProject(this.project.id, request).subscribe({
            next: (updated) => {
                this.saving = false;
                this.dialogRef.close(updated);
            },
            error: (err) => {
                console.error('Error updating project:', err);
                this.saving = false;
                this.snackBar.open(err?.error?.detail || 'Failed to update project', 'Close', { duration: 5000 });
            },
        });
    }
}
